import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { statsAPI, seasonsAPI } from '../api/api';
import SeasonSelector from '../components/SeasonSelector';

const Stats = () => {
  const [category, setCategory] = useState('points');
  const [leaders, setLeaders] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [loading, setLoading] = useState(true);

  const categories = [
    { id: 'points', name: 'Points', stat: 'ppg' },
    { id: 'rebounds', name: 'Rebounds', stat: 'rpg' },
    { id: 'assists', name: 'Assists', stat: 'apg' },
    { id: 'steals', name: 'Steals', stat: 'spg' },
    { id: 'blocks', name: 'Blocks', stat: 'bpg' },
    { id: 'fg', name: 'FG%', stat: 'fgPct' },
    { id: '3pt', name: '3PT%', stat: 'tpPct' }
  ];

  // Load all seasons once, auto-select active or latest
  useEffect(() => {
    const init = async () => {
      try {
        const res = await seasonsAPI.getAll();
        const sorted = [...(res.data || [])].sort((a, b) => b.id - a.id);
        setSeasons(sorted);
        const active = sorted.find(s => s.isActive) || sorted[0];
        if (active) setSelectedSeason(active);
      } catch (err) {
        console.error('Error loading seasons:', err);
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (selectedSeason) loadLeaders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, selectedSeason]);

  const handleSeasonChange = (id) => {
    const season = seasons.find(s => String(s.id) === String(id));
    if (season) setSelectedSeason(season);
  };

  const loadLeaders = async () => {
    setLoading(true);
    try {
      const response = await statsAPI.getLeaders(category, selectedSeason.id, 25);
      setLeaders(response.data);
    } catch (error) {
      console.error('Error loading leaders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentCategory = () => categories.find(c => c.id === category);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-4xl font-bold">Player Statistics</h1>
          {selectedSeason?.completed && (
            <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-semibold rounded uppercase tracking-wide">
              Final
            </span>
          )}
          {selectedSeason?.isActive && (
            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-semibold rounded uppercase tracking-wide">
              Live
            </span>
          )}
        </div>
        <SeasonSelector seasons={seasons} selectedId={selectedSeason?.id} onChange={handleSeasonChange} />
      </div>

      {/* Category Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Select Category</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={`px-4 py-3 rounded-lg font-semibold transition ${
                category === cat.id
                  ? 'bg-nba-blue text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:bg-gray-600'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Leaders Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-6">
        <h2 className="text-2xl font-bold mb-4">
          {getCurrentCategory()?.name} Leaders - {selectedSeason?.name}
        </h2>

        {(category === 'fg' || category === '3pt') && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            * Minimum 10 {category === 'fg' ? 'field goal' : 'three-point'} attempts required
          </p>
        )}

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">Rank</th>
                  <th className="px-4 py-3 text-left">Player</th>
                  <th className="px-4 py-3 text-left">Team</th>
                  <th className="px-4 py-3 text-center">GP</th>
                  <th className={`px-4 py-3 text-center ${category === 'points' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 font-bold' : ''}`}>PPG</th>
                  <th className={`px-4 py-3 text-center ${category === 'rebounds' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 font-bold' : ''}`}>RPG</th>
                  <th className={`px-4 py-3 text-center ${category === 'assists' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 font-bold' : ''}`}>APG</th>
                  <th className={`px-4 py-3 text-center ${category === 'steals' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 font-bold' : ''}`}>SPG</th>
                  <th className={`px-4 py-3 text-center ${category === 'blocks' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 font-bold' : ''}`}>BPG</th>
                  <th className={`px-4 py-3 text-center ${category === 'fg' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 font-bold' : ''}`}>FG%</th>
                  <th className={`px-4 py-3 text-center ${category === '3pt' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 font-bold' : ''}`}>3P%</th>
                </tr>
              </thead>
              <tbody>
                {leaders.map((player, index) => (
                  <tr key={index} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 font-bold text-gray-600 dark:text-gray-400">{index + 1}</td>
                    <td className="px-4 py-3">
                      <Link 
                        to={`/player/${player.player.id}`}
                        className="font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {player.player.firstName} {player.player.lastName}
                      </Link>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {player.player.position} | {player.player.overallRating} OVR
                      </div>
                    </td>
                    <td className="px-4 py-3">{player.team.abbreviation}</td>
                    <td className="px-4 py-3 text-center">{player.games}</td>
                    <td className={`px-4 py-3 text-center font-semibold ${category === 'points' ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>{player.ppg}</td>
                    <td className={`px-4 py-3 text-center ${category === 'rebounds' ? 'bg-blue-50 dark:bg-blue-900/20 font-semibold' : ''}`}>{player.rpg}</td>
                    <td className={`px-4 py-3 text-center ${category === 'assists' ? 'bg-blue-50 dark:bg-blue-900/20 font-semibold' : ''}`}>{player.apg}</td>
                    <td className={`px-4 py-3 text-center ${category === 'steals' ? 'bg-blue-50 dark:bg-blue-900/20 font-semibold' : ''}`}>{player.spg}</td>
                    <td className={`px-4 py-3 text-center ${category === 'blocks' ? 'bg-blue-50 dark:bg-blue-900/20 font-semibold' : ''}`}>{player.bpg}</td>
                    <td className={`px-4 py-3 text-center ${category === 'fg' ? 'bg-blue-50 dark:bg-blue-900/20 font-semibold' : ''}`}>{player.fgPct}%</td>
                    <td className={`px-4 py-3 text-center ${category === '3pt' ? 'bg-blue-50 dark:bg-blue-900/20 font-semibold' : ''}`}>{player.tpPct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Stats;
